import { useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Users } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import StatusPill from '../../components/StatusPill'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { formatDateTime } from '../../utils'

import SectionTitle from './SectionTitle'
import Detail from './Detail'
import { useVisitorDetails } from './useVisitorDetails'
import VisitorLoader from './VisitorLoader'
import Permission from './Permission'
import COILink from './COILink'

const VendorDetailsModal = ({ vendorId, onClose, selectedBuilding }) => {
  const { t } = useTranslation(['visitors', 'common'])
  const { data, isLoading, isError } = useVisitorDetails('vendor', vendorId)

  useEffect(() => {
    if (isError) {
      onClose()
    }
  }, [isError])

  if (!vendorId) {
    return null
  }

  return (
    <ModalContainer
      additionalClass="visitor-modal"
      onClose={onClose}
      headerTitle={t('visitors:detailsModal.guestVendorTitle', {
        type: t('visitors:modalTypes.vendor'),
      })}
      headerIcon={<Users size={20} />}
    >
      {isLoading || !data ? (
        <VisitorLoader />
      ) : (
        <div className="visitor-details">
          {data.visitor.notes && (
            <div className="visitor-details__section">
              <SectionTitle title={t('visitors:detailsModal.note')} />
              <Detail
                label={t('visitors:detailsModal.note')}
                className="visitor-details__section-item--vertical"
                value={data.visitor.notes}
              />
            </div>
          )}

          <div className="visitor-details__section">
            <SectionTitle title={t('visitors:detailsModal.vendorInformation')} />
            <div className="visitor-details__section-items-container">
              <Detail label={t('visitors:detailsModal.vendorName')} value={data.visitor.name} />
              <Detail label={t('visitors:detailsModal.vendorStatus')}>
                <StatusPill status={data.visitor.status} />
              </Detail>
              <Detail label={t('visitors:detailsModal.email')} value={data.visitor.email} />
              <Detail label={t('visitors:detailsModal.phone')} value={data.visitor.phone} />
              <Detail label={t('visitors:detailsModal.company')} value={data.visitor.company} />
              {data.visitor.status === 'banned' && data.visitor.bannedReason && (
                <Detail
                  label={t('visitors:detailsModal.banReason')}
                  className="visitor-details__section-item--vertical"
                  value={data.visitor.bannedReason}
                />
              )}
            </div>
          </div>

          <div className="visitor-details__section">
            <SectionTitle title={t('visitors:detailsModal.permissionAndVisitDetails')} />
            <Detail label={t('visitors:detailsModal.permissionType')} value={data.permissionType}>
              <Permission visitor={data.visitor} timezone={selectedBuilding.timezone} />
            </Detail>
            <Detail
              label={t('visitors:detailsModal.startTime')}
              value={formatDateTime(data.visitor.startTime, selectedBuilding.timezone).time}
            />
            <Detail
              label={t('visitors:detailsModal.endTime')}
              value={formatDateTime(data.visitor.endTime, selectedBuilding.timezone).time}
            />
            <Detail label={t('visitors:detailsModal.createdBy')} value={data.visitor.createdBy} />
            <Detail
              label={t('visitors:detailsModal.permissionToEnter')}
              value={
                data.visitor.permissionToEnter ? t('common:booleans.yes') : t('common:booleans.no')
              }
            />
            <Detail label={t('visitors:detailsModal.coi')}>
              <COILink
                filePath={data.visitor.coi}
                visitorName={data.visitor.name || t('visitors:detailsModal.vendorDefaultName')}
              />
            </Detail>
            <Detail
              label={t('visitors:detailsModal.pinCodeAccess')}
              value={data.visitor.pinCode ? t('common:booleans.yes') : t('common:booleans.no')}
            />
            <Detail
              label={t('visitors:detailsModal.vendorId')}
              value={data.visitor.vendorId || data.visitor.guestId}
            />
          </div>

          {data.checkIns.length ? (
            <div className="visitor-details__section">
              <SectionTitle title={t('visitors:detailsModal.checkInLog')} />
              {data.checkIns.map((log, index) => {
                const date = formatDateTime(log.date, selectedBuilding.timezone)
                return <Detail key={index} label={date.date} value={date.time} />
              })}
            </div>
          ) : null}
        </div>
      )}
    </ModalContainer>
  )
}

VendorDetailsModal.propTypes = {
  vendorId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  selectedBuilding: PropTypes.object,
}

export default connect((state) => {
  return {
    selectedBuilding: selectActiveBuilding(state),
  }
})(VendorDetailsModal)
